import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { FiUserCheck, FiUserX, FiTrash, FiEdit, FiEye } from "react-icons/fi";
import { api, endpoint } from "../../api";
import { Spin } from "antd";
import { useSearchQuery } from "../../utils/useSearchQuery";
import appConfig from "../../appConfig";

const UserList = () => {
  const role = useSearchQuery("role");

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getUsers = async () => {
    setIsLoading(true);
    try {
      let { results, count } = await api.get(endpoint.all_users, {
        params: {
          limit,
          page,
          sortBy: "createdAt",
          orderBy: "DESC",
          role: role !== "all" ? role : null,
        },
      });
      results = results.map((item, idx) => {
        item.sl = (page - 1) * limit + idx + 1;
        return item;
      });
      setUsers(results);
      setTotal(count || 0);
    } catch (error) {}

    setIsLoading(false);
  };

  const onConfirmActiveDeactiveUser = async (row) => {
    const data = {
      isActive: !row.isActive,
    };
    Swal.fire({
      title: "Are you sure?",
      text: "You want to change user status?",
      dangerMode: true,
      showCancelButton: true,
      confirmButtonText: "Confirm",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .put(`user/` + row._id, data, {
            withCredentials: true,
          })
          .then((res) => {
            getUsers();
            Swal.fire({
              title: "Done!",
              text: "User Status Successfully Changed.",
              icon: "success",
              timer: 2000,
              button: false,
            });
          })
          .catch((err) => {});
      } else if (result.dismiss === Swal.DismissReason.cancel) {
      }
    });
  };

  const deleteUser = async (userId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this user?",
      //icon: "warning",
      dangerMode: true,
      showCancelButton: true,
      confirmButtonText: "Confirm",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`user/` + userId, {
            withCredentials: true,
          })
          .then((res) => {
            getUsers();
            Swal.fire({
              title: "Done!",
              text: "User Deleted Successfully",
              icon: "success",
              timer: 2000,
              button: false,
            });
          })
          .catch((err) =>
            Swal.fire({
              title: "",
              text: "User Deleted Failed",
              icon: "error",
              timer: 2000,
              button: false,
            })
          );
      } else if (result.dismiss === Swal.DismissReason.cancel) {
      }
    });
  };

  useEffect(() => {
    document.title = `${appConfig.name} - Users`;
    getUsers();
    // eslint-disable-next-line
  }, [page, limit, role]);

  const columns = [
    {
      name: "No.",
      selector: ({ sl }) => sl,
      width: "60px",
    },
    {
      name: "User Name",
      cell: (row) => (
        <div className="d-flex my-2 align-items-center">
          <div>
            <img
              src={row.avatar}
              width={40}
              height={40}
              className="me-2 rounded-circle"
              style={{ objectFit: "cover" }}
              alt={`${row.name}`}
            />
          </div>
          <p className="m-0">{row?.name || "--"}</p>
        </div>
      ),
      width: "200px",
    },
    {
      name: "Email",
      cell: (row) => row.email,
      width: "200px",
    },
    {
      name: "User Type",
      cell: (row) => <div className="text-capitalize">{row.role}</div>,
      selector: (row) => row.role,
      // width: "100px",
      center: true,
    },
    {
      name: "Date Created",
      selector: (row) => moment(row.createdAt).format("DD/MM/YYYY"),
      center: true,
    },
    {
      name: "Last Login",
      cell: (row) => (
        <div>
          {row?.lastLogin ? (
            <div>
              {moment(row.lastLogin).format("DD/MM/YYYY")}
              <br />
              {moment(row.lastLogin).format("hh:mm:ss A")}
            </div>
          ) : (
            "--"
          )}
        </div>
      ),
      center: true,
    },
    {
      name: "Status",
      cell: (row) => (
        <div>
          {row.isActive ? (
            <span className="badge text-bg-success">Active</span>
          ) : (
            <span className="badge text-bg-danger">Deactivated</span>
          )}
        </div>
      ),
      center: true,
      // width: '100px',
    },
    {
      name: "Action",
      width: "220px",
      center: true,
      cell: (row) => (
        <div>
          <Link
            to={`/users/details/${row._id}?role=${role || "all"}`}
            className="btn btn-success me-1"
          >
            <FiEye title="View Profile" />
          </Link>
          <Link
            to={`/users/edit/${row._id}?role=${role || "all"}`}
            className="btn btn-info me-1"
          >
            <FiEdit title="Edit Profile" />
          </Link>
          {row.isActive ? (
            <button
              className="btn btn-warning  me-1"
              onClick={() => onConfirmActiveDeactiveUser(row)}
            >
              <FiUserX title="Inactive Account" />
            </button>
          ) : (
            <button
              className="btn btn-success  me-1"
              onClick={() => onConfirmActiveDeactiveUser(row)}
            >
              <FiUserCheck title="Active Account" />
            </button>
          )}
          <button
            className="btn btn-danger"
            onClick={() => deleteUser(row._id)}
          >
            <FiTrash title="Delete" />
          </button>
        </div>
      ),
    },
  ];

  const getHeader = (role) => {
    switch (role) {
      case "installer":
        return {
          title: "All Installers",
          btnText: "Add Installer",
          btnLink: "/users/add-public-user",
        };
      case "admin":
        return {
          title: "All Admins",
          btnText: "Add Admin",
          btnLink: "/users/add-public-user",
        };
      case "user":
        return {
          title: "All Site Users",
          btnText: "Add Site User",
          btnLink: "/users/add-public-user",
        };
      case "public":
        return {
          title: "All Public Users",
          btnText: "Add User",
          btnLink: "/users/add-public-user",
        };
      default:
        return {
          title: "All Users",
          btnText: "Add User",
          btnLink: "/users/add-public-user",
        };
    }
  };

  return (
    <div className="card p-3 m-3">
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <label className="az-content-label">Manage Users</label>
          <span className="d-block py-2">{getHeader(role).title}</span>
        </div>
        <div className="col-md-4 d-flex justify-content-end p-0">
          <Link
            className="btn btn-az-primary btn-md text-capitalize"
            to={`/users/create?role=${role || "public"}`}
          >
            Add {role || "user"}
          </Link>
        </div>
      </div>

      <hr className="mt-0" />

      <Spin spinning={isLoading}>
        <DataTable
          columns={columns}
          data={users}
          pagination
          paginationServer
          noDataComponent={isLoading ? "Loading" : "No record found."}
          className="mt-3"
          paginationTotalRows={total}
          paginationPerPage={limit}
          onChangePage={(data) => {
            setPage(data);
          }}
          onChangeRowsPerPage={(data) => {
            setLimit(data);
            setPage(1);
          }}
          paginationRowsPerPageOptions={[10, 20, 50]}
        />
      </Spin>
    </div>
  );
};

export default UserList;
